import * as PIXI from 'pixi.js';
import * as TweenJS from "slotworks/utils/tween";
import Contract from "slotworks/utils/contract";
import ButtonElement, { ButtonEvent } from "slotworks/graphics/elements/button-element";
import Layers from "slotworks/graphics/layers/layers";
import BetOptions from "slotworks/components/betbar/vo/bet-options";
import smartButton, { SmartButtonFlag } from "slotworks/smart-button/smart-button";
import Components from "slotworks/components/components";
import BonusBuyPopups from "./bonus-buy-prompt";
import Point from "slotworks/utils/geom/point";
import betModel from "slotworks/model/bets/bet-model";
import BetMenuMediator from "slotworks/mediators/bet-menu-mediator";
import { parallel, sequence } from "slotworks/utils/race-utils";
import slotDefinition from "slotworks/model/slot-definition";
import revolverModel from "slotworks/integration/revolver/model/revolver-model";
import { log } from "util";
import { AxiosRequestConfig } from "axios";
import commsManager from "slotworks/server/comms-manager";
import { BounceZoomIn, FadeIn, BounceZoomOut, FadeOut } from "slotworks/graphics/animation/base-animations";
import { PIXIElement } from "slotworks/graphics/pixi/group";
import deviceInfo from "slotworks/utils/device-info";
import FSBuyPrompt from "./fs-buy-prompt";
import stateMachine from "slotworks/state-machine/state-machine";
import gameState from "slotworks/model/game-state";
import BuyoutRecord from "integration/revolver/schema/buyout-record";
import canvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import Rectangle from "slotworks/utils/geom/rectangle";

export default class BonusBuyContainer {

    public static MULTIPLIER_FREESPINS = 40;
    public static MULTIPLIER_RESPIN = {
        MINI: 20,
        MAXI: 30,
        MAJOR: 50,
        GRAND: 80
    }

    private closeButton: ButtonElement;
    private prevButton: ButtonElement;
    private nextButton: ButtonElement;

    private inResolve: Function;

    private betOptions: BetOptions;

    private prompts: BonusBuyPopups[];
    private fsprompt: FSBuyPrompt;

    private moving: boolean = false;

    private positions: Point[];
    private scales: number[];

    private enabled: boolean = false;

    private zooming: boolean = false;

    private portraitPotsLayer: PIXI.Container = null;

    private pots: {type: string, pot: PIXI.Sprite, position: Rectangle}[];

    private currentId: number = 0;
    private previous: number = 0;

    private jackpots = [
        {
            type: "MINI",
            buyout: () => this.buyRequest(BuyoutTypes.JACKPOT_MINI)
        },
        {
            type: "MAXI",
            buyout: () => this.buyRequest(BuyoutTypes.JACKPOT_MAXI)
        },
        {
            type: "MAJOR",
            buyout: () => this.buyRequest(BuyoutTypes.JACKPOT_MAJOR)
        },
        {
            type: "GRAND",
            buyout: () => this.buyRequest(BuyoutTypes.JACKPOT_GRAND)
        }
    ]

    public show() {
        return new Contract(resolve => {
            Layers.CelebrationBackground.setScene("bonusbuy").execute();
            const background = Layers.CelebrationBackground.getSprite("fullscreen");
            FadeIn(background, 150).execute();
            this.betOptions = betModel.generateBetOptions();
            Layers.Custom.get("BonusBuyPopups").setScene("bonusbuy").then(() => {
                this.init();
                this.zoomIn().execute();
            });
            smartButton.setFlag(SmartButtonFlag.PROMPT, true);
            Components.betBar.setEnabled(false);
            Layers.BetBar.hide();
            Layers.SmartButton.hide();
            if(!deviceInfo.isDesktop)
                deviceInfo.onLayoutChange.add(this.onLayoutChange);
            this.inResolve = resolve;
        })
    }

    private zoomIn() {
        return BounceZoomIn([...Layers.Custom.get("BonusBuyPopups").container.children] as PIXIElement[], 400);
    }

    private zoomOut() {
        return BounceZoomOut([...Layers.Custom.get("BonusBuyPopups").container.children] as PIXIElement[], 400);
    }


    // private reinit = () => {
    //     setTimeout(() => this.init(), 1);
    // }

    private init() {
        Components.betBar.setEnabled(false);
        Components.betBar.setBetOptions(this.betOptions);

        this.closeButton = Layers.Custom.get("BonusBuyPopups").getButton("close");
        this.closeButton.on(ButtonEvent.POINTER_DOWN, this.close);

        this.prevButton = Layers.Custom.get("BonusBuyPopups").getButton("prev");
        this.prevButton.on(ButtonEvent.POINTER_DOWN, this.prev);

        this.nextButton = Layers.Custom.get("BonusBuyPopups").getButton("next");
        this.nextButton.on(ButtonEvent.POINTER_DOWN, this.next);

        this.prompts = [];

        if(canvasManager.orientation == Orientation.PORTRAIT) {
            this.portraitPotsLayer = new PIXI.Container();
            this.pots = [];
            Layers.Custom.get("BonusBuyPopups").container.addChildAt(this.portraitPotsLayer, 10);
            for(let el of this.jackpots) {
                const pot = Layers.Custom.get("BonusBuyPopups").getSprite(el.type + "Pot");
                const position = Layers.Custom.get("BonusBuyPopups").getPosition(el.type + "Pot");
                pot.anchor.set(0.5, 0.5);
                this.pots.push({
                    type: el.type,
                    pot, 
                    position
                });
                this.portraitPotsLayer.addChild(pot);
            }
        }
        
        for(let el = 0; el < this.jackpots.length - 1; el++) {
            const type = this.jackpots[el];
            const prompt = new BonusBuyPopups(type.type, true, this.betOptions, this.incrementBet, this.decrementBet, type.buyout);
            this.prompts.push(prompt);
            if(canvasManager.orientation == Orientation.PORTRAIT) {
                prompt.zoom(1, 0.6, 0, false, true).execute();
            }
        }

        const el = this.jackpots.length - 1;
        const type = this.jackpots[el];
        const prompt = new BonusBuyPopups(type.type, false, this.betOptions, this.incrementBet, this.decrementBet, type.buyout);
        this.prompts.push(prompt);

        if(canvasManager.orientation == Orientation.PORTRAIT) {
            prompt.zoom(1, 0.6, 0, false, true).execute();
        }
        
        this.positions = [];
        this.scales = []; 

        const pos1 = Layers.Custom.get("BonusBuyPopups").getPosition("pos1");
        const pos2 = Layers.Custom.get("BonusBuyPopups").getPosition("pos2");
        this.positions.push(new Point(prompt.container.x, prompt.container.y));
        this.positions.push(new Point(pos2.x + pos2.width/2, pos2.y + pos2.height/2));
        this.positions.push(new Point(prompt.container.x, prompt.container.y));
        this.positions.push(new Point(pos1.x + pos1.width/2, pos1.y + pos1.height/2));

        this.scales.push(1, 0.56, 0.5, 0.56);

        this.fsprompt = new FSBuyPrompt(this.betOptions, this.incrementBet, this.decrementBet, this.buyFreespin);

        this.currentId = 0;

        this.setPromptsTransform();

        this.updateFields();
    }

    private setPromptsTransform() {
        if(canvasManager.orientation == Orientation.LANDSCAPE) {
            for(let i = 0; i < this.prompts.length; i++) {
                this.prompts[i].container.position.set(this.positions[i].x, this.positions[i].y);
                this.prompts[i].container.scale.set(this.scales[i], this.scales[i]);
                this.prompts[i].setEnabled(false);
            }
            this.prompts[0].moveToTop();
            this.prompts[0].setEnabled(true);
        } else {
            this.prompts[this.currentId].zoom(1, 1, 0, true, true).execute();
            this.prompts[this.currentId].moveToTop();

            const pos1 = Layers.Custom.get("BonusBuyPopups").getPosition("pos1");
            const pos2 = Layers.Custom.get("BonusBuyPopups").getPosition("pos2");

            this.setPotTo(this.pots[3].pot, pos1.x + pos1.width/2, pos1.y + pos1.height/2, 0.8);
            this.setPotTo(this.pots[0].pot, 
                this.pots[0].position.x + this.pots[0].position.width/2, 
                this.pots[0].position.y + this.pots[0].position.height/2,
                1);
            this.setPotTo(this.pots[1].pot, pos2.x + pos2.width/2, pos2.y + pos2.height/2, 0.8);
            this.setPotTo(this.pots[2].pot, 
                this.pots[2].position.x + this.pots[2].position.width/2, 
                this.pots[2].position.y + this.pots[2].position.height/2, 
                0.6);

            moveToTop(this.pots[0].pot);
        }
        
    }

    private setPotTo(pot: PIXI.Sprite, x: number, y: number, scale: number) {
        pot.position.set(x, y);
        pot.scale.set(scale, scale);
    }

    private destroy() {
        this.closeButton.off(ButtonEvent.POINTER_DOWN, this.close);
        this.prevButton.off(ButtonEvent.POINTER_DOWN, this.prev);
        this.nextButton.off(ButtonEvent.POINTER_DOWN, this.next);

        if(!deviceInfo.isDesktop)
            deviceInfo.onLayoutChange.remove(this.onLayoutChange);
    }

    private next = () => {
        if(!this.moving) {

            if(canvasManager.orientation == Orientation.PORTRAIT) {
                const pot = this.pots.shift();
                this.pots.push(pot);
                
                this.previous = this.currentId;
                this.currentId++;

                if(this.currentId >= this.prompts.length) {
                    this.currentId = 0;
                }
            } else {
                const prompt = this.prompts.shift();
                this.prompts.push(prompt);
            }

            this.updatePositions();
        }
    }

    private onLayoutChange = () => {
        setTimeout(() => this.init(), 1);
    }

    private prev = () => {
        if(!this.moving) {
            
            if(canvasManager.orientation == Orientation.PORTRAIT) {
                const pot = this.pots.pop();
                this.pots.unshift(pot);
                this.previous = this.currentId;
                this.currentId--;

                if(this.currentId < 0) {
                    this.currentId = this.prompts.length - 1;
                }
            } else {
                const prompt = this.prompts.pop();
                this.prompts.unshift(prompt);
            }

            this.updatePositions();
        }
    }

    public movePotTo(pot: PIXI.Sprite, x:number, y: number, endScale: number, time: number): Contract<void> {
        return new Contract(resolve => {
            this.setEnabled(false);
            new TweenJS.Tween(pot)
            .to({x: x, y: y}, time)
            .onComplete(resolve)
            .start();
            new TweenJS.Tween(pot.scale)
            .to({x: endScale, y: endScale}, time - 10)
            .start();
        });  
    }

    private updatePortrait() {
        const funcs = [];
        const seqFunc: (() => Contract<void>)[] = [];
        seqFunc.push(() => this.prompts[this.previous].zoom(1, 0.6, 200, false));
        seqFunc.push(() => this.prompts[this.currentId].zoom(0.6, 1, 200, true));


        const pos1 = Layers.Custom.get("BonusBuyPopups").getPosition("pos1");
        const pos2 = Layers.Custom.get("BonusBuyPopups").getPosition("pos2");

        funcs.push(() => this.movePotTo(this.pots[3].pot, pos1.x + pos1.width/2, pos1.y + pos1.height/2, 0.8, 400));
        funcs.push(() => this.movePotTo(this.pots[0].pot, 
            this.pots[0].position.x + this.pots[0].position.width/2, 
            this.pots[0].position.y + this.pots[0].position.height/2, 
            1, 400));
        funcs.push(() => this.movePotTo(this.pots[1].pot, pos2.x + pos2.width/2, pos2.y + pos2.height/2, 0.8, 400));
        funcs.push(() => this.movePotTo(this.pots[2].pot, 
            this.pots[2].position.x + this.pots[2].position.width/2, 
            this.pots[2].position.y + this.pots[2].position.height/2,
            0.6, 400));
        funcs.push(() => sequence(seqFunc))

        moveToTop(this.pots[0].pot)

        return parallel(funcs);
    }

    private updatePositions() {
        this.moving = true;
        if(canvasManager.orientation == Orientation.PORTRAIT) {
            this.updatePortrait().then(() => {
                this.moving = false;
            });
        } else {
            const funcs = [];
            this.prompts[0].moveToTop();
            for(let i = 0; i < this.prompts.length; i++) {
                funcs.push(() => this.prompts[i].moveTo(this.positions[i].x, this.positions[i].y, this.scales[i], 400, i === 0));
            }
            parallel(funcs).then(() => {
                this.moving = false;
            });
        };
    }

    private buyFreespin = () => {
        this.buyRequest(BuyoutTypes.FREESPIN);
    }

    private incrementBet = () => {
        let index = this.betOptions.totalStakeValues.indexOf(this.betOptions.getTotalStake());
        if(index < this.betOptions.totalStakeValues.length + 1) {
            index++;
            const currentStake = this.betOptions.totalStakeValues[index];
            this.betOptions.setTotalStake(currentStake);
            this.updateFields();
            this.updateBet();
        }
    }

    private decrementBet = () => {
        let index = this.betOptions.totalStakeValues.indexOf(this.betOptions.getTotalStake());
        if(index > 0) {
            index--;
            const currentStake = this.betOptions.totalStakeValues[index];
            this.betOptions.setTotalStake(currentStake);
            this.updateFields();
            this.updateBet();
        }
    }

    private updateFields() {
        this.fsprompt.updateFields();
        for(let prompt of this.prompts) {
            prompt.updateFields();
        }
    }

    private updateBet() {
        betModel.setCoinSize(this.betOptions.coinSize, true);
        betModel.setBetPerLine(this.betOptions.stakePerLine, true);

        Components.betBar.setCoinSize(this.betOptions.coinSize);
        Components.betBar.setStake(this.betOptions.stakePerLine);
        Components.paytable.refreshBetOptions();
    }

    private afterPrompt() { 
        Components.betBar.setEnabled(true);

        const betMenuMediator = new BetMenuMediator();
        betMenuMediator.start().execute();
        betMenuMediator.end();
    }

    private setEnabled(enabled: boolean) {

    }

    private close = () => {
        this.destroy();
        this.zoomOut().then(() => {
            sequence([
            () => Layers.Custom.get("BonusBuyPopups").defaultScene(),
            () => FadeOut(Layers.CelebrationBackground.getSprite("fullscreen"), 200),
            () => Layers.CelebrationBackground.defaultScene()
            ]).execute();
            if(this.inResolve) {
                this.inResolve(false);
                this.inResolve = null;
            }
            smartButton.setFlag(SmartButtonFlag.PROMPT, false);
            Components.betBar.setEnabled(true);
            Layers.BetBar.show();
            Layers.SmartButton.show();
        });
    }

    private buyRequest(type: BuyoutTypes) {
        this.setEnabled(false);
        this.buyRequestAction(type).then(() => { 
            const record = gameState.getCurrentGame().latestRecord() as BuyoutRecord;
            if (record) {
                if (record && record.type) {
                    this.destroy();
                    sequence([
                        () => this.zoomOut(),
                        () => Layers.Custom.get("BonusBuyPopups").defaultScene(),
                        () => FadeOut(Layers.CelebrationBackground.getSprite("fullscreen"), 200),
                        () => Layers.CelebrationBackground.defaultScene()
                        ]).then(() => {                            
                            smartButton.setFlag(SmartButtonFlag.PROMPT, false);
                            if (this.inResolve) {
                                this.inResolve(true);
                                this.inResolve = null;
                            }
                            stateMachine.skip();
                    });
                }
            }      
        });
    }

    private buyRequestAction(type: BuyoutTypes): Contract<void> {
        return new Contract(resolve => {
            this.betOptions = betModel.generateBetOptions();
            const coinLevel = slotDefinition.coinSizes.indexOf(this.betOptions.coinSize) + 1;
            const action: any = {
                type: type,
                bet: { bet_level: this.betOptions.stakePerLine / revolverModel.creditBaseMultiplier, coin_level: coinLevel }
            };

            const requestPayLoad: any = {
                cmd: "RESULTS",
                additionalData: {},
                session: revolverModel.session,
                action
            };

            try {
                if (revolverModel.getGameCode()) {
                    requestPayLoad.additionalData.gameCode = revolverModel.getGameCode();
                }
                if (revolverModel.getOperator()) {
                    requestPayLoad.additionalData.getOperator = revolverModel.getOperator();
                }
                if (revolverModel.getOperatorId()) {
                    requestPayLoad.additionalData.getOperatorId = revolverModel.getOperatorId();
                }
                if (revolverModel.getBranding()) {
                    requestPayLoad.additionalData.getBranding = revolverModel.getBranding();
                }
                if (window && window.location) {
                    if (window.location.hasOwnProperty("pathname")) {
                        requestPayLoad.additionalData.windowPath = window.location.pathname;
                    }
                }
                if (revolverModel.sendBetConfigInResult) {
                    requestPayLoad.additionalData.betConfig = revolverModel.betConfig;
                }
            } catch (e) {
                log(e);
            }

            const requestConfig = {
                method: "post",
                url: revolverModel.getBaseURL(),
                withCredentials: true,
                data: JSON.stringify(requestPayLoad)
            } as AxiosRequestConfig;

            if (revolverModel.jwt_token) {
                requestConfig.headers = {
                    "X-RGS-SESSION-TOKEN": revolverModel.jwt_token
                };
            }

            commsManager.requestRaw(requestConfig).then(resolve);
        })
    }

}

function moveToTop(sprite: PIXI.Sprite) {
    const parent = sprite.parent;
    parent.removeChild(sprite);
    parent.addChild(sprite);
}

export enum BuyoutTypes {
    FREESPIN = "BUYOUT_SPINS",
    JACKPOT_MINI = "BUYOUT_RESPIN_MINI",
    JACKPOT_MAXI = "BUYOUT_RESPIN_MAXI",
    JACKPOT_MAJOR = "BUYOUT_RESPIN_MAJOR",
    JACKPOT_GRAND = "BUYOUT_RESPIN_GRAND"
}


// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./src/components/bonus-buy/bonus-buy-container.ts